import React, { memo, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useAuth, useDebounce, useFeatureFlags } from '@node-space/hooks'
import { Callout, Box as CalloutContainer, Tab, Tabs } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { DEFAULT_NUMBER_OF_ROWS, PathNames, SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useShowVerifiedBannerMutation } from 'hooks/mutations/useShowVerifiedBannerMutation'
import { useAccountPreferencesQuery } from 'hooks/queries'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { TransactionsPagination } from 'pages/WalletDetails/components/RecentActivity/TransactionsPagination'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { TWalletType, WalletTypes } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import track from 'utils/tracker'
import { getWalletPropertiesForCapabilityChecks } from 'utils/wallet'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { useGetTotalBalance } from '../../hooks/queries/useGetTotalBalance'
import WalletModal, { WalletAction } from './components/Modal/WalletModal'
import WalletTable from './components/Table/WalletTable'
import WalletTotalBalance from './components/Table/WalletTotalBalance'
import WalletHeaderButtons from './components/WalletHeaderButtons'
import { WalletCreateModalProvider } from './context/WalletCreateModalProvider'
import { WalletControllerProvider } from './context/WalletProvider'

const Wallets = () => {
  const { t } = useTranslation()
  const { enableFiatCurrencyFilter, enableFiatPayinCapabilityV2, enableFiatPayoutCapabilityV2 } =
    useFeatureFlags()

  const queryClient = useQueryClient()

  const permissions = useMappedRoles()?.wallets
  const isEditDisabled = !permissions?.canEdit
  const canTrade = permissions?.canTrade

  const [page, setPage] = useState(0)
  const [searchPage, setSearchPage] = useState(0)
  const [offset, setOffset] = useState(0)
  const [numOfRows, setNumOfRows] = useState(DEFAULT_NUMBER_OF_ROWS)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [closedVerifiedBanner, setClosedVerifiedBanner] = useState(false)
  const [showWalletModal, setShowWalletModal] = useState<boolean>(false)
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(undefined)

  const { tab } = queryString.parse(window.location.search) as { tab?: TWalletType }
  const [activeTab, setActiveTab] = useState(tab || WalletTypes.FIAT)

  const { mutate: putShowVerifiedBanner } = useShowVerifiedBannerMutation()

  const {
    data: accountPreferences,
    isPending: isLoadingAccountPreferences,
    isError: isErrorAccountPreferences,
  } = useAccountPreferencesQuery()

  const { currentAccount, isLoading: isLoadingCurrentAccount } = useAccountsContext()
  const { isAuthed } = useAuth()

  const { profile, viewOnly } = useProfileContext() || {}
  const { currencyCode } = profile || {}

  const baseCurrency = accountPreferences?.currency || currencyCode

  const showVerifiedBanner = useMemo(
    () => !isLoadingAccountPreferences && accountPreferences?.showVerifiedBanner,
    [isLoadingAccountPreferences, accountPreferences]
  )

  const debouncedSearch = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const {
    data: walletsData,
    isSuccess: isWalletSuccess,
    isLoading: isLoadingWallets,
    isError: isErrorWallets,
  } = useWalletsQueryV2({
    walletType: activeTab,
    searchTerm: debouncedSearch,
    offset,
    max: Number(numOfRows),
  })

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (
      activeTab?.toLowerCase() === WalletTypes.FIAT &&
      !!walletsData?.data?.length &&
      currentAccount?.reference &&
      isAuthed &&
      !isLoadingCurrentAccount
    ) {
      const walletLsids = walletsData?.data?.map(wallet => wallet?.lsid)

      return getWalletActionCapabilityRequestFilters(
        walletLsids,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [activeTab, walletsData, currentAccount?.reference, isAuthed, isLoadingCurrentAccount])

  const { data: bulkCapabilities, isLoading: isLoadingWalletsCapabilities } =
    useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  const wallets = useMemo(() => {
    if (activeTab === WalletTypes.FIAT) {
      return walletsData?.data?.map(wallet => {
        const walletProperties = getWalletPropertiesForCapabilityChecks(wallet)

        const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
          getFiatWalletActionSupport({
            walletProperties,
            bulkCapabilities,
            enableFiatCurrencyFilter,
          })

        return {
          ...wallet,
          supportsDeposits: supportsDeposits,
          supportsWithdrawals: supportsWithdrawals,
          supportsThirdParty: supportsPay,
          supportsInternalTransfers: supportsInternalTransfers,
        }
      })
    } else {
      return walletsData?.data
    }
  }, [bulkCapabilities, walletsData, activeTab])

  const { data: totalBalance, isFetching: isFetchingTotalBalance } =
    useGetTotalBalance(baseCurrency)

  const hasErrors = isErrorWallets || isErrorAccountPreferences

  const isWalletBeingSearched = !!searchQuery?.length

  const switchToTab = (tab: TWalletType) => {
    // add query param to current URL without causing page reload
    window.history.pushState({}, '', `?tab=${tab}`)
    setActiveTab(tab)
  }

  const tabs: Tab[] = [
    {
      id: WalletTypes.FIAT,
      header: t('wallets.fiat'),
      content: null,
      onClick: () => switchToTab(WalletTypes.FIAT),
      testId: 'fiat-wallets-tab',
      ...(totalBalance?.fiatWalletsNumber && { badgeCount: totalBalance?.fiatWalletsNumber }),
    },
    {
      id: WalletTypes.CRYPTO,
      header: t('wallets.crypto'),
      content: null,
      onClick: () => switchToTab(WalletTypes.CRYPTO),
      testId: 'crypto-wallets-tab',
      ...(totalBalance?.cryptoWalletsNumber && { badgeCount: totalBalance?.cryptoWalletsNumber }),
    },
  ]

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.totalBalance(baseCurrency) })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setPage(0)
    setSearchPage(0)
    setOffset(0)
  }

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.WALLET_HOME, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    if (isWalletBeingSearched) {
      setSearchPage(0)
      setOffset(0)
    }
  }, [isWalletBeingSearched])

  const verifyWidgetCloseButton = () => {
    setClosedVerifiedBanner(true)
    putShowVerifiedBanner(false)
  }

  return (
    <WalletControllerProvider
      isLoading={
        isLoadingWallets || (activeTab === WalletTypes.FIAT && isLoadingWalletsCapabilities)
      }
      isSuccess={isWalletSuccess}
      hasErrors={hasErrors}
      baseCurrency={baseCurrency}
      totalBalance={totalBalance?.total}
      totalFiat={totalBalance?.totalFiat}
      totalCrypto={totalBalance?.totalCrypto}
    >
      <WalletCreateModalProvider
        condition={location.pathname === `${PathNames.WALLETS}${PathNames.WALLET_CREATE}`}
      >
        {showVerifiedBanner && !closedVerifiedBanner && (
          <CalloutContainer paddingB={16}>
            <Callout onClose={verifyWidgetCloseButton} variant="success">
              {t('verification.successMessage')}
            </Callout>
          </CalloutContainer>
        )}

        <PageHeader
          description={<WalletTotalBalance isFetchingTotalBalance={isFetchingTotalBalance} />}
          renderRightSection={
            <WalletHeaderButtons
              canEdit={!isEditDisabled}
              canTrade={canTrade}
              viewOnly={viewOnly}
            />
          }
        />

        <Tabs tabs={tabs} defaultTabId={activeTab} />

        <WalletTable
          searchQuery={searchQuery}
          onSearchTermChange={setSearchQuery}
          wallets={wallets || []}
          walletType={activeTab}
          setWalletModalAction={setWalletModalAction}
          testid={`${activeTab}-wallet-table`}
          setShowWalletModal={setShowWalletModal}
          clearFilters={clearFilters}
          isWalletBeingSearched={isWalletBeingSearched}
          debouncedSearch={debouncedSearch}
        />

        {!!walletsData?.data?.length &&
          !isLoadingWallets &&
          walletsData?.paging?.total / Number(numOfRows) > 1 && (
            <TransactionsPagination
              page={isWalletBeingSearched ? searchPage : page}
              setPage={isWalletBeingSearched ? setSearchPage : setPage}
              setOffset={setOffset}
              pagination={walletsData?.paging}
              nrOfRows={numOfRows}
              setNrOfRows={setNumOfRows}
            />
          )}

        {showWalletModal && (
          <WalletModal
            action={walletModalAction}
            setWalletModalAction={setWalletModalAction}
            refresh={refreshWallets}
            setShowWalletModal={setShowWalletModal}
          />
        )}
      </WalletCreateModalProvider>
    </WalletControllerProvider>
  )
}

export default memo(Wallets)
